import type { CollectibleId } from "@/services/collectibles/collectiblesTypes";
import type { CategoryId } from "@/services/collection/categories/categoriesSchema";
import { createCollectionItem, updateCollectionItem } from "@/services/collection/item/collectionItemApi";
import type { CollectionItem } from "@/services/collection/item/collectionItemTypes";
import { orderFrontBackGalleryImages, uploadFrontBackPhotos, type FrontBackPhotos } from "@/services/collection/item/gallery/api";
import { batchSetCollectionItemParameters, type ParameterSetRequest } from "@/services/parameters/parametersApi";
import { withRetry } from "@/utilities/retry";

export type CreateCollectionItemFromCollectibleOptions = {
  collectibleId: CollectibleId;
  categoryId: CategoryId;
  photos?: FrontBackPhotos;
  aiIdentificationSourceId?: string;
  publicNote?: string;
  primaryParams?: ParameterSetRequest[];
};

export const createCollectionItemFromCollectible = async (options: CreateCollectionItemFromCollectibleOptions): Promise<CollectionItem> => {
  const item = await withRetry(() =>
    createCollectionItem({
      categoryId: options.categoryId,
      collectibleId: options.collectibleId,
      set: options.collectibleId,
      minifigure: options.collectibleId,
      userCollectionListName: "",
    })
  );

  const tasks: Promise<unknown>[] = [];
  let photoIds: Awaited<ReturnType<typeof uploadFrontBackPhotos>> = {};

  if (options.photos) {
    tasks.push(
      uploadFrontBackPhotos(item, options.photos).then((ids) => {
        photoIds = ids;
      })
    );
  }

  const aiIdentificationSourceId = options.aiIdentificationSourceId;
  if (aiIdentificationSourceId !== undefined) {
    tasks.push(withRetry(() => updateCollectionItem(item.id, { aiIdentificationSourceId })));
  }

  const publicNote = options.publicNote;
  if (publicNote !== undefined) {
    tasks.push(withRetry(() => updateCollectionItem(item.id, { publicNote })));
  }

  const primaryParams = options.primaryParams;
  if (primaryParams && primaryParams.length > 0) {
    tasks.push(withRetry(() => batchSetCollectionItemParameters(item.id, primaryParams)));
  }

  await Promise.all(tasks);
  await orderFrontBackGalleryImages(photoIds);

  return item;
};
